import { useEffect, type RefObject } from "react";

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

/**
 * Drives the Business Profile page's scroll-linked header chrome — top bar
 * fade-in, compact title reveal, banner parallax/fade, "Change banner"
 * fade-out, and the tab bar's "stuck" shadow — entirely through CSS custom
 * properties written on the page root. React itself never re-renders on
 * scroll: every tick just calls `style.setProperty()` on the DOM node, so
 * this is safe to run continuously against a long product grid.
 *
 * Passive + rAF-throttled: at most one write per animation frame.
 *
 * `rootRef` may not be attached yet on the very first ticks (e.g. the
 * business is still loading, so the real `.biz-listings-page` tree hasn't
 * mounted) — each tick just re-checks `rootRef.current` and no-ops until it
 * exists, so no separate "enabled" wiring is needed here or at the call site.
 */
export function useBusinessHeaderScroll(rootRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Single source of truth for the fixed top bar's rendered height. The
    // tab bar's `position: sticky` offset (`top`) and the "has it actually
    // reached that offset yet" check below both need this same number —
    // previously each one hardcoded its own guess (60px / 60.5px) while the
    // bar's real height (padding + back-button + border) comes out closer
    // to 64px, so the two could drift out of sync whenever the bar's CSS
    // changed. Measuring once here and publishing it as a CSS custom
    // property means both sides always agree, automatically.
    let topbarHeight = 0;

    const measureTopbarHeight = () => {
      const root = rootRef.current;
      const topbar = root?.querySelector<HTMLElement>(".biz-listings-topbar--fixed");
      if (!root || !topbar) return;
      topbarHeight = topbar.getBoundingClientRect().height;
      root.style.setProperty("--biz-topbar-height", `${topbarHeight}px`);
    };

    const applyScrolledEndState = () => {
      const root = rootRef.current;
      if (!root) return;
      measureTopbarHeight();
      root.style.setProperty("--biz-topbar-alpha", "1");
      root.style.setProperty("--biz-topbar-blur", "14");
      root.style.setProperty("--biz-title-opacity", "1");
      root.style.setProperty("--biz-title-shift", "0px");
      root.style.setProperty("--biz-banner-parallax", "0px");
      root.style.setProperty("--biz-banner-fade", "0.4");
      root.style.setProperty("--biz-banner-upload-opacity", "0");
      root.style.setProperty("--biz-tabbar-shadow", "0 4px 12px rgba(20, 20, 40, 0.08)");
    };

    if (prefersReducedMotion) {
      // No motion — jump straight to the settled "scrolled" chrome once so
      // it still reads correctly without ever animating toward it.
      applyScrolledEndState();
      return;
    }

    let ticking = false;

    const tick = () => {
      ticking = false;
      const root = rootRef.current;
      if (!root) return; // real page not mounted yet — next scroll tick retries

      // Only re-measure once mounted (topbarHeight starts at 0) or on an
      // explicit resize tick — the bar's height doesn't change with scroll
      // position, so there's no need to read it on every single frame.
      if (topbarHeight === 0) measureTopbarHeight();

      const scrollTop = document.scrollingElement?.scrollTop ?? 0;

      const topbarAlpha = clamp01((scrollTop - 40) / 60); // fades in 40–100px
      const topbarBlur = topbarAlpha * 14; // px

      const titleOpacity = clamp01((scrollTop - 120) / 50); // fades in 120–170px
      const titleShift = (1 - titleOpacity) * 8; // px, settles to 0

      const bannerParallax = scrollTop * 0.45;
      const bannerFade = 1 - clamp01(scrollTop / 150) * 0.6; // settles at ~40% opacity

      const uploadOpacity = 1 - clamp01(scrollTop / 70);

      root.style.setProperty("--biz-topbar-alpha", String(topbarAlpha));
      root.style.setProperty("--biz-topbar-blur", String(topbarBlur));
      root.style.setProperty("--biz-title-opacity", String(titleOpacity));
      root.style.setProperty("--biz-title-shift", `${titleShift}px`);
      root.style.setProperty("--biz-banner-parallax", `${bannerParallax}px`);
      root.style.setProperty("--biz-banner-fade", String(bannerFade));
      root.style.setProperty("--biz-banner-upload-opacity", String(uploadOpacity));

      // Detected via getBoundingClientRect rather than relying on
      // `:has(:stuck)`/scroll-timeline support — a plain, well-supported way
      // to tell whether the (CSS `position: sticky`) tab bar has actually
      // hit its `top` offset yet, without touching React state. Compares
      // against the SAME measured topbarHeight used for the CSS `top`
      // offset above, not a separate constant.
      const tabbar = root.querySelector<HTMLElement>(".biz-tabbar");
      const stuck =
        !!tabbar && topbarHeight > 0 && tabbar.getBoundingClientRect().top <= topbarHeight + 0.5;
      root.style.setProperty(
        "--biz-tabbar-shadow",
        stuck ? "0 4px 12px rgba(20, 20, 40, 0.08)" : "none"
      );
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(tick);
    };

    const onResize = () => {
      // Button/padding sizes can change at a different breakpoint — force a
      // re-measure rather than trusting the last one.
      measureTopbarHeight();
      onScroll();
    };

    // Establish the correct chrome immediately — covers a deep link that
    // lands already scrolled (the existing `?tab=` auto-scroll effect) as
    // well as an ordinary top-of-page load.
    tick();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [rootRef]);
}
